import { t } from "i18next";
import { Bell, BellOff, ChevronDown, ChevronUp } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import RemindersAPI, { Reminders } from "../../../api/RemindersAPI";
import { ButtonV2 } from "../../../components/ButtonV2/ButtonV2";
import Card from "../../../components/Card/Card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../../../components/Collapsible/Collapsible";
import PrimaryButton from "../../../components/PrimaryButton/PrimaryButton";
import SectionTitle from "../../../components/SectionTitle/SectionTitle";
import {
  BookingConfirmationEmail,
  BookingConfirmationEmailConfig,
} from "../reminders/BookingConfirmationEmail/BookingConfirmationEmail";
import { BrandIntroReminder, BrandIntroReminderConfig } from "../reminders/BrandIntroReminder/BrandIntroReminder";
import { PostVisitEmail, PostVisitEmailConfig } from "../reminders/PostVisitEmail/PostVisitEmail";
import { VisitReminder } from "../reminders/VisitReminder/VisitReminder";
import { WeddingRateReminder } from "../reminders/WeddingRateReminder/WeddingRateReminder";
import type { NotificationChannel } from "../../../components/NotificationChannelToggle/NotificationChannelToggle";
import type { EmailBodyFormat } from "../../../types/emailBodyFormat";

export interface VisitReminderConfig {
  enabled: boolean;
  type: 'sms' | 'email';
  details?: {
    // phoneId?: string; // Commented out - not currently used in backend SMS sending
    emailTitle?: string;
    text?: string;
    daysInAdvance?: number;
    emailBodyFormat?: EmailBodyFormat;
  };
}

export interface WeddingRateConfig {
  enabled: boolean;
  type?: NotificationChannel;
  emailTitle: string;
  text: string;
  emailBodyFormat?: EmailBodyFormat;
}

export type ReminderConfig =
  | VisitReminderConfig
  | WeddingRateConfig
  | BrandIntroReminderConfig
  | PostVisitEmailConfig
  | BookingConfirmationEmailConfig;

export type UpdateCallback = (reminder: string, config: ReminderConfig) => void;

function NotificationTemplateCollapsible({
  title,
  enabled,
  children,
}: {
  title: string;
  enabled: boolean;
  children: ReactNode;
}) {
  const { t: tCollapsible } = useTranslation();
  const [open, setOpen] = useState(false);
  const statusLabel = enabled
    ? String(tCollapsible("global.enabled"))
    : String(tCollapsible("global.disabled"));
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <ButtonV2
          type="button"
          variant="ghost"
          className="w-full justify-between gap-2 h-auto py-2 px-1 text-left hover:bg-stone-50 items-center"
          aria-label={`${title}, ${statusLabel}`}
        >
          <span className="flex min-w-0 flex-1 items-center gap-2 text-left">
            {enabled ? (
              <Bell className="h-5 w-5 shrink-0 text-emerald-600" aria-hidden />
            ) : (
              <BellOff className="h-5 w-5 shrink-0 text-slate-400" aria-hidden />
            )}
            <SectionTitle text={title} as="span" cssStyles="min-w-0 flex-1 text-left" />
          </span>
          {open ? (
            <ChevronUp className="h-5 w-5 shrink-0 text-slate-500" aria-hidden />
          ) : (
            <ChevronDown className="h-5 w-5 shrink-0 text-slate-500" aria-hidden />
          )}
        </ButtonV2>
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-1 pb-2">{children}</CollapsibleContent>
    </Collapsible>
  );
}

function NotificationsSegment() {
  const { t: tHook } = useTranslation();

  const [hasChanges, setHasChanges] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [reminders, setReminders] = useState<Reminders>({
    smsReminder: undefined,
    weddingRateReminder: undefined,
    brandIntroReminder: undefined,
    postVisitEmail: undefined,
    bookingConfirmationEmail: undefined,
  });

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const fetchedReminders = await RemindersAPI.get();
        setReminders(fetchedReminders.data);
      } catch (error) {
        console.error("Failed to fetch initial reminders data:", error);
      }
    };
    loadInitialData();
  }, []); 

  const updateReminders = (reminder: string, config: ReminderConfig) => {
    setHasChanges(true);
    setReminders(prevReminders => {
      return {
        ...prevReminders,
        [reminder]: config,
      };
    });
  };

  const visitEnabled =
    Boolean(reminders.smsReminder && "type" in reminders.smsReminder && reminders.smsReminder.enabled);
  const weddingEnabled = reminders.weddingRateReminder?.enabled ?? false;
  const brandIntroEnabled = reminders.brandIntroReminder?.enabled ?? false;
  const postVisitEnabled = reminders.postVisitEmail?.enabled ?? false;
  const bookingConfirmationEnabled = reminders.bookingConfirmationEmail?.enabled ?? false;

  const saveChanges = async () => {
    setIsLoading(true);

    try {
      await RemindersAPI.update(reminders);
      setHasChanges(false);
    } catch (error) {
      console.error("Failed to update reminders:", error);
    }
    setIsLoading(false);
  };

  return (
      <>
        <div className="flex flex-col gap-2">
        <Card>
          <NotificationTemplateCollapsible
            title={String(tHook("notifications.visit_reminder"))}
            enabled={visitEnabled}
          >
            <VisitReminder
              hideSectionTitle
              update={updateReminders}
              defaultConfig={reminders.smsReminder && 'type' in reminders.smsReminder ? reminders.smsReminder : undefined}
            />
          </NotificationTemplateCollapsible>
        </Card>
        <Card>
          <NotificationTemplateCollapsible
            title={String(tHook("notifications.wedding_rate_reminder"))}
            enabled={weddingEnabled}
          >
            <WeddingRateReminder hideSectionTitle update={updateReminders} defaultConfig={reminders.weddingRateReminder || {}} />
          </NotificationTemplateCollapsible>
        </Card>
        <Card>
          <NotificationTemplateCollapsible
            title={String(tHook("notifications.post_visit"))}
            enabled={postVisitEnabled}
          >
            <PostVisitEmail hideSectionTitle update={updateReminders} defaultConfig={reminders.postVisitEmail} />
          </NotificationTemplateCollapsible>
        </Card>
        <Card>
          <NotificationTemplateCollapsible
            title={String(tHook("notifications.booking_confirmation"))}
            enabled={bookingConfirmationEnabled}
          >
            <BookingConfirmationEmail
              hideSectionTitle
              update={updateReminders}
              defaultConfig={reminders.bookingConfirmationEmail}
            />
          </NotificationTemplateCollapsible>
        </Card>
        <Card>
          <NotificationTemplateCollapsible
            title={String(tHook("notifications.brand_intro"))}
            enabled={brandIntroEnabled}
          >
            <BrandIntroReminder hideSectionTitle update={updateReminders} defaultConfig={reminders.brandIntroReminder} />
          </NotificationTemplateCollapsible>
        </Card>
        </div>
        <div className="container flex justify-center flex-wrap space-x-5 mt-4">
          <PrimaryButton text={t("global.save")!} fn={saveChanges} isDisabled={!hasChanges || isLoading} />
        </div>
      </>
  );
}

export default NotificationsSegment;
